import { FAMILY_ROLE, FamilyData } from '~/familyAccount/types/familyAccounts.types'
import { IUser } from '~/types/user-info-types'
import appConfig from '~/config/app'

/**
 * Returns the canonical email for impact.com tracking, mirroring the
 * precedent in src/utils/tags.ts:71 (facebook → auth → email fallback chain).
 */
export function getCanonicalEmailForTracking(user: IUser | null | undefined): string {
  return (user?.facebookEmail ?? user?.authEmail ?? user?.email ?? '').trim().toLowerCase()
}

/**
 * Returns true if the current user is a learner in a family account and
 * should be excluded from impact.com attribution tracking. Family admins
 * and users not in a family return false.
 */
export function isImpactChild(currentFamilyUser: FamilyData | null | undefined): boolean {
  return currentFamilyUser?.role === FAMILY_ROLE.LEARNER
}

/**
 * SHA-1 hash via Web Crypto SubtleCrypto. Returns empty string if:
 * - input is falsy
 * - running on the server (typeof window === 'undefined')
 * - the provided AbortSignal is already aborted
 */
export async function sha1Hex(input: string, signal?: AbortSignal): Promise<string> {
  if (!input) return ''
  if (typeof window === 'undefined') return ''
  if (signal?.aborted) return ''

  const encoded = new TextEncoder().encode(input)
  const buffer = await window.crypto.subtle.digest('SHA-1', encoded)
  return Array.from(new Uint8Array(buffer), (b) => b.toString(16).padStart(2, '0')).join('')
}

interface ImpactIdentifyPayload {
  customerId: string
  customerEmail: string
  customProfileId: string
}

/**
 * Pushes an impact_identify event to window.dataLayer.
 * GTM consumes this event and fires ire('identify', ...).
 * Empty strings (not undefined/null) for unknown fields.
 */
export function pushImpactIdentify({ customerId, customerEmail, customProfileId }: ImpactIdentifyPayload): void {
  if (typeof window === 'undefined') return
  if (appConfig.isSchoolPlatform) return
  window.dataLayer = window.dataLayer ?? []
  window.dataLayer.push({ event: 'impact_identify', customerId, customerEmail, customProfileId })
}

interface ImpactTrialStartPayload {
  orderId: string
  customerId: string
  customerEmail: string
  customProfileId: string
  currencyCode: string
}

/**
 * Pushes an impact_trial_start event to window.dataLayer.
 * GTM consumes this event and fires ire('trackConversion', ...).
 * Empty strings (not undefined/null) for unknown fields.
 */
export function pushImpactTrialStart({
  orderId,
  customerId,
  customerEmail,
  customProfileId,
  currencyCode,
}: ImpactTrialStartPayload): void {
  if (typeof window === 'undefined') return
  if (appConfig.isSchoolPlatform) return
  window.dataLayer = window.dataLayer ?? []
  window.dataLayer.push({
    event: 'impact_trial_start',
    orderId,
    customerId,
    customerEmail,
    customProfileId,
    currencyCode,
  })
}
